/**
 * The location of technical indicator data on a tech chart.
 */
export var TechChartChartLocation;
(function (TechChartChartLocation) {
    // Displayed on a y-axis separate from ohlc data
    TechChartChartLocation["indicator"] = "indicator";
    // Displayed on the same y-axis as the ohlc data
    TechChartChartLocation["overlay"] = "overlay";
})(TechChartChartLocation || (TechChartChartLocation = {}));
/**
 * Options that apply to the chart as a whole
 */
export var ChartOptions;
(function (ChartOptions) {
    ChartOptions["ADJUSTED"] = "adjusted";
    ChartOptions["SHOW_US_RECESSIONS"] = "recessions";
    ChartOptions["USE_LOG_SCALE"] = "scaleType";
    ChartOptions["NAME_IN_LEGEND"] = "nameInLegend";
    ChartOptions["SHOW_UNITS_IN_LEGEND"] = "units";
    ChartOptions["QUOTE_LEGEND"] = "quoteLegend";
    ChartOptions["LEGEND_ON_CHART"] = "legendOnChart";
    ChartOptions["DISPLAY_DATE_RANGE"] = "displayDateRange";
    ChartOptions["HIDE_ZOOM_BAR"] = "hideZoomBar";
    ChartOptions["SHOW_WATERMARK"] = "showWatermark";
    ChartOptions["SHOW_PERFORMANCE_DISCLOSURE"] = "performanceDisclosure";
})(ChartOptions || (ChartOptions = {}));
/**
 * Technical Chart Style
 */
export var ChartStyle;
(function (ChartStyle) {
    // Display OHLC as candlestick chart
    ChartStyle["CANDLESTICK"] = "candlestick";
    // Display closing price
    ChartStyle["LINE"] = "line";
    // Display OHLC as bar chart
    ChartStyle["OHLC"] = "ohlc";
})(ChartStyle || (ChartStyle = {}));
/**
 * Options for scale type
 */
export var TechChartScaleType;
(function (TechChartScaleType) {
    TechChartScaleType["LINEAR"] = "linear";
    TechChartScaleType["LOG"] = "log";
})(TechChartScaleType || (TechChartScaleType = {}));
export var ChartType;
(function (ChartType) {
    // Legend and tooltips update according to the user's mouse location
    ChartType["INTERACTIVE"] = "interactive";
    // Static Image
    ChartType["PRESENTATION"] = "presentation";
})(ChartType || (ChartType = {}));
// TODO: Rename to TechChartFrequency
/**
 * Options that apply to frequency
 * Please note: We also may need to add additional options depending on
 * where we land on presenting intraday data and potentially doing
 * things like 1 hour, etc.
 */
export var Frequency;
(function (Frequency) {
    Frequency["DAILY"] = "market_daily";
    Frequency["WEEKLY"] = "weekly";
    Frequency["MONTHLY"] = "monthly";
})(Frequency || (Frequency = {}));
/**
 * How much tech chart data is displayed
 */
export var TechChartDataAmount;
(function (TechChartDataAmount) {
    TechChartDataAmount["DAILY"] = "daily";
    TechChartDataAmount["SINGLE_DAY_INTRADAY"] = "single_day_intraday";
    TechChartDataAmount["MULTI_DAY_INTRADAY"] = "multi_day_intraday";
    TechChartDataAmount["MULTI_WEEK"] = "multi_week";
    TechChartDataAmount["MULTI_MONTH"] = "multi_month";
    TechChartDataAmount["MULTI_YEAR"] = "multi_year";
})(TechChartDataAmount || (TechChartDataAmount = {}));
/**
 * Types of securities whose data can be charted in a technical chart
 */
export var TechChartSecurityType;
(function (TechChartSecurityType) {
    TechChartSecurityType["company"] = "company";
    TechChartSecurityType["index"] = "index";
})(TechChartSecurityType || (TechChartSecurityType = {}));
/**
 * Names of Technical Indicators
 *
 * Used by the backend to identify technical indicators.
 * These are similar to (but not the same as!) TechChartDataSeriesType.
 */
export var TechnicalType;
(function (TechnicalType) {
    TechnicalType["AROON_OSCILLATOR"] = "aroon_oscillator";
    TechnicalType["AROON"] = "aroon";
    TechnicalType["ATR"] = "atr";
    TechnicalType["CCI"] = "cci";
    TechnicalType["CMF"] = "cmf";
    TechnicalType["DMI"] = "dmi";
    TechnicalType["ROC"] = "roc";
    TechnicalType["STOCHASTIC_FULL"] = "stochastic_full";
    TechnicalType["STOCHASTIC_SLOW"] = "stochastic_slow";
    TechnicalType["STOCHASTIC_FAST"] = "stochastic_fast";
    TechnicalType["WILLIAMS_R"] = "williams_r";
    TechnicalType["SMA"] = "sma";
    TechnicalType["EMA"] = "ema";
    TechnicalType["MA_OSCILLATOR"] = "ma_oscillator";
    TechnicalType["BOLLINGER"] = "bollinger";
    TechnicalType["VOLUME"] = "volume";
    TechnicalType["AVERAGE_VOLUME"] = "average_volume";
    TechnicalType["RSI"] = "rsi";
    TechnicalType["MACD"] = "macd";
    TechnicalType["ROLLING_VOL"] = "rolling_vol";
    TechnicalType["MOMENTUM_OSCILLATOR"] = "momentum_oscillator";
    TechnicalType["ICHIMOKU"] = "ichimoku";
    TechnicalType["MA_ENVELOPES"] = "ma_envelopes";
    TechnicalType["KELTNER_CHANNELS"] = "keltner_channels";
    TechnicalType["CHANDELIER_EXIT_LONG"] = "chandelier_exit_long";
    TechnicalType["CHANDELIER_EXIT_SHORT"] = "chandelier_exit_short";
    TechnicalType["STOCHASTIC_RSI"] = "stochastic_rsi";
    TechnicalType["TRIX"] = "trix";
    TechnicalType["OBV"] = "obv";
})(TechnicalType || (TechnicalType = {}));
export var TechnicalCategory;
(function (TechnicalCategory) {
    TechnicalCategory["OVERLAY"] = "overlay";
    TechnicalCategory["OSCILLATOR"] = "oscillator";
    TechnicalCategory["OTHER"] = "other";
})(TechnicalCategory || (TechnicalCategory = {}));
/**
 * Names used by Highstock to identify technical indicators.
 * These values can be assigned to Highstock.Options.series.type.
 */
export var TechChartDataSeriesType;
(function (TechChartDataSeriesType) {
    // OHLC
    TechChartDataSeriesType["hollowcandlestick"] = "hollowcandlestick";
    TechChartDataSeriesType["ohlc"] = "ohlc";
    TechChartDataSeriesType["line"] = "line";
    // Technicals
    TechChartDataSeriesType["aroonoscillator"] = "aroonoscillator";
    TechChartDataSeriesType["aroon"] = "aroon";
    TechChartDataSeriesType["atr"] = "atr";
    TechChartDataSeriesType["cci"] = "cci";
    TechChartDataSeriesType["cmf"] = "cmf";
    TechChartDataSeriesType["dmi"] = "dmi";
    TechChartDataSeriesType["roc"] = "roc";
    TechChartDataSeriesType["stochastic"] = "stochastic";
    TechChartDataSeriesType["slowstochastic"] = "slowstochastic";
    TechChartDataSeriesType["williamsr"] = "williamsr";
    TechChartDataSeriesType["sma"] = "sma";
    TechChartDataSeriesType["ema"] = "ema";
    TechChartDataSeriesType["bb"] = "bb";
    TechChartDataSeriesType["volume"] = "volume";
    TechChartDataSeriesType["rsi"] = "rsi";
    TechChartDataSeriesType["macd"] = "macd";
    TechChartDataSeriesType["momentum"] = "momentum";
    TechChartDataSeriesType["ikh"] = "ikh";
    TechChartDataSeriesType["priceenvelopes"] = "priceenvelopes";
    TechChartDataSeriesType["keltnerchannels"] = "keltnerchannels";
    TechChartDataSeriesType["trix"] = "trix";
    TechChartDataSeriesType["obv"] = "obv";
})(TechChartDataSeriesType || (TechChartDataSeriesType = {}));
/**
 * Formats for displaying data in the legend.
 */
export var TechChartLegendItemDisplayStyle;
(function (TechChartLegendItemDisplayStyle) {
    // Display the OHLC values
    TechChartLegendItemDisplayStyle["OHLC"] = "ohlc";
    // Display only the close value
    TechChartLegendItemDisplayStyle["OHLC_LINE"] = "ohlc_line";
    // Display the calculated value
    TechChartLegendItemDisplayStyle["TECHNICAL"] = "technical";
    // Display an array of multiple value types
    TechChartLegendItemDisplayStyle["MULTI_VALUE"] = "multi_value";
})(TechChartLegendItemDisplayStyle || (TechChartLegendItemDisplayStyle = {}));
export var XOHLCDataIndex;
(function (XOHLCDataIndex) {
    XOHLCDataIndex[XOHLCDataIndex["X_VAL"] = 0] = "X_VAL";
    XOHLCDataIndex[XOHLCDataIndex["OPEN"] = 1] = "OPEN";
    XOHLCDataIndex[XOHLCDataIndex["HIGH"] = 2] = "HIGH";
    XOHLCDataIndex[XOHLCDataIndex["LOW"] = 3] = "LOW";
    XOHLCDataIndex[XOHLCDataIndex["CLOSE"] = 4] = "CLOSE";
})(XOHLCDataIndex || (XOHLCDataIndex = {}));
export var TechnicalCalcDataIndex;
(function (TechnicalCalcDataIndex) {
    TechnicalCalcDataIndex[TechnicalCalcDataIndex["X_VAL"] = 0] = "X_VAL";
    TechnicalCalcDataIndex[TechnicalCalcDataIndex["CALC"] = 1] = "CALC";
})(TechnicalCalcDataIndex || (TechnicalCalcDataIndex = {}));
export var VolumeDataIndex;
(function (VolumeDataIndex) {
    VolumeDataIndex[VolumeDataIndex["X_VAL"] = 0] = "X_VAL";
    VolumeDataIndex[VolumeDataIndex["VOLUME"] = 1] = "VOLUME";
})(VolumeDataIndex || (VolumeDataIndex = {}));
export var TechnicalKey;
(function (TechnicalKey) {
    TechnicalKey["TYPE"] = "type";
    TechnicalKey["PARAMS"] = "params";
    TechnicalKey["INCLUDE"] = "include";
    TechnicalKey["COLOR"] = "color";
})(TechnicalKey || (TechnicalKey = {}));
export var TechChartSecurityKey;
(function (TechChartSecurityKey) {
    TechChartSecurityKey["COLOR"] = "color";
    TechChartSecurityKey["ID"] = "id";
})(TechChartSecurityKey || (TechChartSecurityKey = {}));
export var TechChartDateSelection;
(function (TechChartDateSelection) {
    TechChartDateSelection["RANGE"] = "range";
    TechChartDateSelection["YEAR"] = "year";
})(TechChartDateSelection || (TechChartDateSelection = {}));
export var TechChartStatus;
(function (TechChartStatus) {
    TechChartStatus["ok"] = "ok";
    TechChartStatus["error"] = "error";
    TechChartStatus["loading"] = "loading";
    TechChartStatus["noData"] = "noData";
    TechChartStatus["getStarted"] = "getStarted";
})(TechChartStatus || (TechChartStatus = {}));
export var TechChartPresentationValidModes;
(function (TechChartPresentationValidModes) {
    TechChartPresentationValidModes["imageCode"] = "image_code";
    TechChartPresentationValidModes["imageCodeTemplate"] = "image_code_template";
    TechChartPresentationValidModes["imageContents"] = "image_contents";
    TechChartPresentationValidModes["imageDownload"] = "image_download";
    TechChartPresentationValidModes["imageAttachment"] = "image_attachment";
    TechChartPresentationValidModes["imageUrl"] = "image_url";
})(TechChartPresentationValidModes || (TechChartPresentationValidModes = {}));
export var TechChartAnnotationName;
(function (TechChartAnnotationName) {
    // text annotation
    TechChartAnnotationName["TEXT"] = "Text";
    // shape annotations
    TechChartAnnotationName["CIRCLE"] = "Circle";
    TechChartAnnotationName["RECTANGLE"] = "Rectangle";
    // line annotations
    TechChartAnnotationName["TREND_SEGMENT"] = "Trend Segment";
    TechChartAnnotationName["ARROW_SEGMENT"] = "Arrow Segment";
    TechChartAnnotationName["RAY"] = "Ray";
    TechChartAnnotationName["ARROW_RAY"] = "Arrow Ray";
    TechChartAnnotationName["LINE"] = "Line";
    TechChartAnnotationName["ARROW_LINE"] = "Arrow Line";
    TechChartAnnotationName["HORIZONTAL_LINE"] = "Horizontal Line";
    TechChartAnnotationName["VERTICAL_LINE"] = "Vertical Line";
    // Counter label annotations
    TechChartAnnotationName["LABEL_COUNTER"] = "Label Counter";
    TechChartAnnotationName["VERTICAL_COUNTER"] = "Vertical Counter";
    TechChartAnnotationName["ARROW_COUNTER"] = "Arrow Counter";
    // Cursor annotations
    TechChartAnnotationName["CURSOR"] = "cursor";
    TechChartAnnotationName["CROSSHAIR"] = "crosshair";
    // Crooked annotations
    TechChartAnnotationName["CROOKED_LINE_3"] = "Crooked Line 3";
    TechChartAnnotationName["CROOKED_LINE_5"] = "Crooked Line 5";
    TechChartAnnotationName["ELLIOTT_3"] = "Elliott 3";
    TechChartAnnotationName["ELLIOTT_5"] = " Elliott 5";
    // Tool measure annotations
    TechChartAnnotationName["MEASURE_XY"] = "Measure XY";
    TechChartAnnotationName["MEASURE_X"] = "Measure X";
    TechChartAnnotationName["MEASURE_Y"] = "Measure Y";
    // Tool adv annotations
    TechChartAnnotationName["PITCHFORK"] = "Pitchfork";
    TechChartAnnotationName["PARALLEL_CHANNEL"] = "Parallel Channel";
    TechChartAnnotationName["FIBONACCI"] = "Fibonacci";
})(TechChartAnnotationName || (TechChartAnnotationName = {}));
// Cursor annotations
export var TechChartCursorType;
(function (TechChartCursorType) {
    TechChartCursorType["CURSOR"] = "cursor";
    TechChartCursorType["CROSSHAIR"] = "crosshair";
})(TechChartCursorType || (TechChartCursorType = {}));
